import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../../hooks/use-toast';
import { 
  Copy, 
  Share2, 
  Users, 
  Gift, 
  Clock, 
  CheckCircle, 
  RefreshCw,
  Link as LinkIcon,
  Award,
  TrendingUp
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const ReferralTab = () => {
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState(null);
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState({
    total_referrals: 0,
    successful_referrals: 0,
    total_membership_days_earned: 0,
    recent_referrals: []
  });
  const [regenerating, setRegenerating] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch referral code
      const codeResponse = await fetch(`${API_BASE}/api/trainers/referral-code`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (codeResponse.ok) {
        const codeData = await codeResponse.json();
        setReferralCode(codeData.code);
        setReferralLink(codeData.referral_link);
      }
      
      // Fetch stats
      const statsResponse = await fetch(`${API_BASE}/api/trainers/referral-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load referral data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: `${type} copied to clipboard`,
    });
  };

  const regenerateCode = async () => {
    try {
      setRegenerating(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE}/api/trainers/referral-code/regenerate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReferralCode(data.code);
        setReferralLink(data.referral_link);
        setShowRegenerateDialog(false);
        toast({
          title: 'Success',
          description: 'Your referral code has been regenerated',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to regenerate code',
        variant: 'destructive'
      });
    } finally {
      setRegenerating(false);
    }
  };

  const shareReferral = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Join TrainerList',
        text: `Join TrainerList with my referral code and get 1 month FREE membership! Use code: ${referralCode}`,
        url: referralLink
      });
    } else {
      copyToClipboard(referralLink, 'Referral link');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Gift className="w-8 h-8" />
          <h2 className="text-2xl font-bold">Referral Program</h2>
        </div>
        <p className="text-purple-100">
          Invite friends to TrainerList and earn 1 month FREE membership for each successful referral!
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Total Referrals</p>
                <p className="text-3xl font-bold text-blue-700 dark:text-blue-300">{stats.total_referrals}</p>
              </div>
              <Users className="w-10 h-10 text-blue-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 dark:text-green-400 font-medium">Successful</p>
                <p className="text-3xl font-bold text-green-700 dark:text-green-300">{stats.successful_referrals}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-200 dark:border-amber-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Days Earned</p>
                <p className="text-3xl font-bold text-amber-700 dark:text-amber-300">{stats.total_membership_days_earned}</p>
              </div>
              <Award className="w-10 h-10 text-amber-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Referral Code Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-red-600" />
            Your Referral Code
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Code Display */}
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Your Code</p>
              <p className="text-2xl font-mono font-bold text-gray-900 dark:text-white tracking-wider">
                {referralCode}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(referralCode, 'Code')}
              className="gap-2"
            >
              <Copy className="w-4 h-4" />
              Copy
            </Button>
          </div>

          {/* Referral Link */}
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Referral Link</p>
            <div className="flex gap-2">
              <Input
                value={referralLink}
                readOnly
                className="font-mono text-sm bg-gray-50 dark:bg-gray-800"
              />
              <Button
                variant="outline"
                onClick={() => copyToClipboard(referralLink, 'Link')}
                className="shrink-0"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              onClick={shareReferral}
              className="gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>
            <Button
              onClick={() => setShowRegenerateDialog(true)}
              variant="outline"
              size="sm"
              disabled={regenerating}
              className="gap-2 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
              Regenerate Code
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            How It Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <Share2 className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-semibold mb-1">1. Share Your Code</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Share your unique referral code or link with friends
              </p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-semibold mb-1">2. They Sign Up</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                New users sign up using your referral code
              </p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <Gift className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-semibold mb-1">3. Both Get Rewarded</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                You both get 1 month FREE membership!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Referrals */}
      {stats.recent_referrals.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Recent Referrals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recent_referrals.map((referral, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      referral.reward_given 
                        ? 'bg-green-100 dark:bg-green-900/30' 
                        : 'bg-yellow-100 dark:bg-yellow-900/30'
                    }`}>
                      {referral.reward_given ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-yellow-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {referral.user_name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {referral.user_email}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-medium ${
                      referral.reward_given 
                        ? 'text-green-600' 
                        : 'text-yellow-600'
                    }`}>
                      {referral.reward_given ? '+30 days' : 'Pending'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(referral.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Regenerate Confirmation Dialog */}
      <AlertDialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate Referral Code?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to regenerate your referral code? Your old code will stop working and any shared links will become invalid.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={regenerateCode} disabled={regenerating}>
              {regenerating ? 'Regenerating...' : 'Yes, Regenerate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReferralTab;
